import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import DataTable from "../../Components/Accounting/DataTable";
import Select2 from "../../Components/Accounting/Select2";
import { TableSkeleton } from "../../Components/Accounting/SkeletonLoader";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";

const GeneralLedger = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(searchParams.get("account_id") || "");
  const [ledgerData, setLedgerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => { fetchAccounts(); }, []);
  useEffect(() => { if (selectedAccount) fetchLedger(); }, [selectedAccount, fromDate, toDate]);

  const fetchAccounts = async () => {
    const res = await ajaxAccounting.listAccounts({ postable_only: 1 });
    if (res.status === "OK") setAccounts(res.details || []);
  };

  const fetchLedger = async () => {
    setLoading(true);
    const res = await ajaxAccounting.getLedger({ account_id: parseInt(selectedAccount), from_date: fromDate || undefined, to_date: toDate || undefined });
    if (res.status === "OK") setLedgerData(res.details);
    else toast.error(res.message || "Failed to load ledger");
    setLoading(false);
  };

  const transactions = ledgerData?.transactions || [];
  const openingBalance = ledgerData?.opening_balance || 0;

  // The running balance comes from the server, which knows whether the account
  // grows on the debit or the credit side — recomputing it here as
  // debit-minus-credit read backwards on every liability and income account.
  const transactionsWithBalance = transactions;
  const closingBalance = ledgerData?.closing_balance ?? openingBalance;

  const columns = [
    { key: "entry_date", label: "Date", render: (v) => functions.formatDate(v) },
    {
      key: "reference", label: "Ref / Source",
      render: (v, row) => row.journal_entry_id ? (
        <button
          type="button"
          className="btn btn-link p-0 tx-12"
          onClick={() => navigate(`/accounting/journal/${row.journal_entry_id}`)}
          title="View journal entry"
        >
          {v || row.source_type || `#${row.journal_entry_id}`}
        </button>
      ) : (
        <span className="tx-12">{v || row.source_type || "-"}</span>
      ),
    },
    { key: "description", label: "Description" },
    { key: "debit", label: "Debit (Dr)", render: (v) => v > 0 ? <span className="tx-success fw-bold">{functions.formatCurrency(v)}</span> : "-" },
    { key: "credit", label: "Credit (Cr)", render: (v) => v > 0 ? <span className="tx-danger fw-bold">{functions.formatCurrency(v)}</span> : "-" },
    // The balance is signed in the account's own direction, so a positive
    // figure on a liability is a credit, not a debit.
    { key: "running_balance", label: "Balance", render: (v) => {
      const normal = ledgerData?.normal_balance === "credit" ? "Cr" : "Dr";
      const opposite = normal === "Cr" ? "Dr" : "Cr";
      return (
        <span className={v < 0 ? "tx-danger" : "tx-primary"}>
          {functions.formatCurrency(Math.abs(v))} {v < 0 ? opposite : normal}
        </span>
      );
    } },
  ];

  return (
    <AppContainer
      title="General Ledger"
      previousPageTitle="Accounting"
      description="View the complete transaction history for any account. Opening balance is shown first, followed by each posted transaction with running balance."
      actions={[]}
    >
      <Toaster />
      <div className="card custom-card p-3 mb-3">
        <div className="row g-2">
          <div className="col-lg-5">
            <label className="form-label tx-12 fw-semibold">Account</label>
            <Select2 options={accounts.map((a) => ({ value: String(a.id), label: `${a.account_code} — ${a.name}` }))} value={selectedAccount} onChange={setSelectedAccount} placeholder="Select account" />
          </div>
          <div className="col-lg-3">
            <label className="form-label tx-12 fw-semibold">From Date</label>
            <input type="date" className="form-control" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div className="col-lg-3">
            <label className="form-label tx-12 fw-semibold">To Date</label>
            <input type="date" className="form-control" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
        </div>
      </div>

      {selectedAccount && ledgerData && (
        <div className="row mb-3">
          {[
            { label: "Opening Balance", value: openingBalance, color: "#6366f1" },
            { label: "Total Debit", value: transactions.reduce((s, t) => s + (t.debit || 0), 0), color: "#10b981" },
            { label: "Total Credit", value: transactions.reduce((s, t) => s + (t.credit || 0), 0), color: "#ef4444" },
            { label: "Closing Balance", value: closingBalance, color: "#0ea5e9" },
          ].map((c, i) => (
            <div key={i} className="col-lg-3 col-sm-6 mb-2">
              <div className="card custom-card p-3" style={{ borderLeft: `3px solid ${c.color}` }}>
                <div className="tx-11 tx-muted mb-1">{c.label}</div>
                <div className="fw-bold tx-16" style={{ color: c.color }}>{functions.formatCurrency(c.value)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card custom-card p-3">
        {!selectedAccount && <div className="text-center py-5 tx-muted">Select an account to view its ledger.</div>}
        {selectedAccount && loading && <TableSkeleton rows={8} cols={6} />}
        {selectedAccount && !loading && (
          <>
            <div className="mb-3 p-2" style={{ background: "var(--light)", borderRadius: "6px" }}>
              <span className="tx-12 fw-semibold">Opening Balance: </span>
              <span className="tx-primary fw-bold">{functions.formatCurrency(openingBalance)}</span>
            </div>
            <DataTable columns={columns} data={transactionsWithBalance} loading={false} pageSize={20} emptyMessage="No transactions for this account in the selected period." />
          </>
        )}
      </div>
    </AppContainer>
  );
};

export default GeneralLedger;
